import styled, { css } from 'styled-components/macro';

import {
  numbersTypography,
  primaryTypography,
} from 'styles/global/typography/typography';
import modularScale from 'styles/helpers/modular-scale';

import { StyledDiv } from 'components/base/AnimatedLoadingWrapper';
import { DragHandler } from 'components/Selector/DSPSelector/SelectedDSPList/DSPItem/DSPItem';

export const Content = styled('div')`
  margin: ${({ theme }): string =>
    `4.125em ${theme.spacing.small} ${theme.spacing.small}`};

  @media screen and (max-width: 40em) {
    margin: 16px 8px;
  }

  @media screen and (min-width: 60em) {
    margin: 3.56rem;
  }
`;

export const Main = styled.div.attrs({
  role: 'main',
})`
  position: relative;
  z-index: 1;
  flex: auto;
  display: flex;
  flex-flow: column nowrap;
  background-color: ${({ theme }): string => theme.LightGrey};
`;

export const HeaderContainer = styled(StyledDiv)`
  display: flex;
  align-items: center;
  padding: calc(100px - ${({ theme }): string => theme.spacing.xlarge}) 0
    calc(55px - ${({ theme }): string => theme.spacing.xlarge}) 0;
  transition: ${({ theme }): string =>
    `opacity ${theme.transitionDuration} ${theme.timingFunction}`};
  opacity: ${({ loading }): number => (loading ? 0 : 1)};
`;

export const Header = styled('header')<{ simpleTextTopBar: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  align-items: center;
  padding: ${({ theme, simpleTextTopBar }): string =>
      simpleTextTopBar ? theme.spacing.large : theme.spacing.xlarge}
    0;
  text-align: center;
  color: ${({ theme }): string => theme.White};

  @media screen and (min-width: 40em) {
    text-align: left;
    flex-flow: row nowrap;
    padding: ${({ theme }): string => theme.spacing.xxlarge} 0;
    justify-content: center;
  }
`;

export const ContentWrapper = styled('div')`
  @media screen and (min-width: 40em) {
    width: auto;
  }
`;

export const Title = styled('div')`
  font-size: 20px;
  line-height: 28px;
  padding: 0 55px;

  @media screen and (min-width: 40em) {
    font-size: 44px;
    line-height: 45px;
    padding: 0;
  }
`;

export const AccordionLayout = styled('div')`
  margin: 0 auto;
  background-color: ${({ theme }): string => theme.White};
  padding: 22px 30px;

  @media screen and (max-width: 40em) {
    padding: 0;
  }

  @media screen and (min-width: 60em) {
    max-width: calc(100% - (2em * 2));
  }

  @media screen and (min-width: 80em) {
    max-width: calc(100% - (8em * 2));
  }
`;

export const FAQRow = styled.div<any>`
  display: flex;
  flex-direction: column;
  opacity: ${({ isDragging }): number => (isDragging ? 0.3 : 1)};
  p {
    margin: 0;
  }
`;

export const FAQItemTitle = styled.div<{ activeTitle: boolean }>`
  height: 50px;
  display: flex;
  flex-flow: row wrap;
  border-bottom: 1px solid ${({ theme }): string => theme.Grey};
  ${numbersTypography};
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }): string => theme.NightBlue};
  padding: 0 20px;
  align-items: center;

  &:first-of-type {
    border-radius: 4px 4px 0 0;
  }

  &:hover {
    cursor: pointer;
    background: ${({ theme }): string => theme.Sand};
  }

  ${({ activeTitle }): any =>
    activeTitle &&
    css`
      background: ${({ theme }): string => theme.Sand};
    `}

  @media screen and (max-width: 40em) {
    height: 45px;
    font-size: 16px;
    font-weight: 400;
    padding: 0 13px;
  }
`;

export const FAQItemContent = styled.div`
  background: rgba(247, 247, 247, 0.5);
  padding: 0 20px 20px;
  border-bottom: 1px solid ${({ theme }): string => theme.Grey};

  &,
  h3,
  p {
    line-height: 1.4;
  }
`;

export const H2 = styled('h2')`
  font-size: ${modularScale(2)};
  margin-bottom: 0.25em;
  margin-top: 2em;
`;

export const H3 = styled('h3')`
  font-size: ${modularScale(0)};
  margin-bottom: 0.5em;
  margin-top: 2em;

  @media screen and (max-width: 40em) {
    line-height: calc(${modularScale(0)} * 1.5);
  }
`;

export const H4 = styled('h4')`
  font-size: ${modularScale(-1)};
`;

export const DragHandlerWrapper = styled('div')`
  margin-left: ${({ theme }): string => theme.spacing.medium};
  margin-right: -15px;

  ${DragHandler} {
    display: flex;
  }
`;

export const ButtonsWrapper = styled('div')`
  margin-left: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const EditButton = styled('div')`
  font-size: 14px;
  ${primaryTypography}
  font-weight: normal;
  color: #06a580;
  padding: ${({ theme }): string => `0.8em ${theme.spacing.xlarge}`};
`;

export const TextArea = styled('textarea')<any>`
  width: 100%;
  font: inherit;
  appearance: none;
  box-shadow: none;
  outline: none;
  border: 1px solid ${({ theme }): string => theme.CasperGrey};
  padding: 19px 20px;

  ::-ms-clear {
    display: none;
  }
  &:focus {
    border: 1px solid ${({ theme }): string => theme.LightNightBlue};
  }
`;

export const Input = styled('input')<any>`
  appearance: none;
  box-shadow: none;
  outline: none;
  width: 100%;
  font: inherit;
  height: 42px;
  display: flex;
  align-content: center;
  border: 1px solid ${({ theme }): string => theme.CasperGrey};
  padding: 0 12px;

  ::-ms-clear {
    display: none;
  }
  &:focus {
    border: 1px solid ${({ theme }): string => theme.LightNightBlue};
  }
`;

export const LabelArea = styled('div')`
  font-size: 14px;
  ${primaryTypography}
  font-weight: normal;
  color: ${({ theme }): string => theme.CasperGrey};
  margin: ${({ theme }): string =>
    `${theme.spacing.large} 0 ${theme.spacing.xxsmall}`};
`;

const buttonStyles = css`
  appearance: none;
  font-size: 12px;
  padding: 6px 15px;
  margin: 0.5em 0;
  border: 0;
  font-weight: normal;

  &:not(:first-child) {
    margin-left: 1em;
  }
`;

export const PrimaryButton = styled('button')`
  ${buttonStyles};
  color: ${({ theme }): string => theme.White};
  background-color: ${({ theme }): string => theme.AccentGreen};

  svg {
    margin: -8px 1px -8px -10px;
  }
`;

export const CancelButton = styled('button')`
  ${buttonStyles};
  color: ${({ theme }): string => theme.AccentGreen};

  &:hover {
    background-color: ${({ theme }): string => theme.Sand};
  }
`;

export const DeleteButton = styled('button')`
  ${buttonStyles};
  color: #ff7171;
  margin-left: auto !important;

  &:hover {
    background-color: ${({ theme }): string => theme.Sand};
  }
`;

export const EditContentButtonWrapper = styled('div')`
  display: flex;
`;

export const ModalContentWrapper = styled('div')`
  padding: 0 45px 35px;
`;

export const ModalTitle = styled('div')`
  padding: 45px 45px 25px;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }): string => theme.NightBlue};
`;
